import { clean, money, num, stripHtml } from './quinn';

export interface FeeFacts {
  appFee: string | null;
  adminFee: string | null;
  /** Security deposit value and its note: ["Varies", "with screening"] or ["$500", ""]. */
  deposit: [string, string] | null;
  depositAlt: boolean;
  /** "$232.94–$252.94" summed from the required monthly charges. */
  monthlyRange: string | null;
  /** The required monthly charges as written, plus "Renters insurance required." when the field says so. */
  monthlyText: string | null;
  /** The whole field, when it could not be read into parts. */
  raw: string | null;
}

const cents = (n: number) =>
  Number.isInteger(n) ? money(n)! : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * property_fees_deposit_info is free text, written the same way on every Quinn community today:
 * "Security deposit varies with application screening; a security deposit alternative is available.
 *  Required monthly charges: utility billing $11.99, …, landscaping $95 (single-family home) or $75 (townhome), …;
 *  renters insurance required. …". Anything it can't read falls back to the field as written.
 */
export function feeFacts(p: Record<string, unknown>): FeeFacts {
  const info = stripHtml(clean(p.property_fees_deposit_info) || '');
  const app = num(clean(p.property_application_fee));
  const admin = num(clean(p.property_admin_fee));

  let deposit: [string, string] | null = null;
  if (/security deposit varies/i.test(info)) deposit = ['Varies', /screening/i.test(info) ? 'with screening' : ''];
  else {
    const m = /security deposit[^.;$]*?\$([\d,]+(?:\.\d+)?)/i.exec(info);
    if (m) deposit = [cents(num(m[1])!), ''];
  }

  let monthlyRange: string | null = null;
  let monthlyText: string | null = null;
  const m = /required monthly charges:\s*([\s\S]+?)(?:;|\.(?=\s|$))/i.exec(info);
  if (m) {
    let lo = 0;
    let hi = 0;
    m[1].split(/,\s*/).forEach((item) => {
      const amounts = [...item.matchAll(/\$([\d,]+(?:\.\d+)?)/g)].map((x) => num(x[1])!).filter((x) => x != null);
      if (!amounts.length) return;
      lo += Math.min(...amounts);
      hi += Math.max(...amounts);
    });
    if (lo) monthlyRange = hi > lo + 0.001 ? `${cents(lo)}–${cents(hi)}` : cents(lo);
    const text = m[1].trim();
    monthlyText = `${text[0].toUpperCase()}${text.slice(1)}.${/renters insurance required/i.test(info) ? ' Renters insurance required.' : ''}`;
  }

  return {
    appFee: app ? money(app) : null,
    adminFee: admin ? money(admin) : null,
    deposit,
    depositAlt: /deposit alternative[^.;]*available/i.test(info),
    monthlyRange,
    monthlyText,
    raw: info && !m && !deposit ? info : null,
  };
}

export interface FeeLine {
  name: string;
  amount: string;
  applied: string;
  freq: string;
  note?: string;
  /** Lowest and highest amount, for totals. */
  lo?: number;
  hi?: number;
}

export interface FeeSchedule {
  moveIn: FeeLine[];
  monthly: FeeLine[];
  addOns: FeeLine[];
  /** Known move-in charges for one adult (application + admin), before the deposit. */
  moveInTotal: number | null;
  monthlyLo: number | null;
  monthlyHi: number | null;
}

const NAMES: Record<string, string> = { concierge: 'Concierge Fee', 'smart home': 'Smart Home Package', trash: 'Trash', internet: 'Internet' };
const titleWords = (s: string) => s.replace(/\b[a-z]/g, (c) => c.toUpperCase());

interface FeeItem {
  name?: string;
  description?: string;
  amount?: number | string;
  amount_max?: number | string;
  amount_raw?: string;
  timing?: string;
  is_required?: boolean | string;
}

/** One-pet and two-pet fees when the pet policy description states both ("$300 for one pet, $500 for two"). */
export function petFeeTiers(p: Record<string, unknown>): [number, number] | null {
  const desc = stripHtml(clean(p.property_pet_policy_description) || '');
  const two = /\$([\d,]+)\s*for one pet,?\s*\$([\d,]+)\s*for two/i.exec(desc) || /\$([\d,]+)\s*for one pet\s*\(\$([\d,]+)\s*for two/i.exec(desc);
  return two ? [num(two[1])!, num(two[2])!] : null;
}

/**
 * The Easy Fee Guide schedule. A property with a fees collection uses it; Quinn communities have none today, so the
 * lines come from the fee fields, the "Required monthly charges" sentence and the pet fields.
 */
export function feeSchedule(p: Record<string, unknown>): FeeSchedule {
  const f = feeFacts(p);
  const items = ((p.fees as FeeItem[]) || []).filter((x) => clean(x.name));
  const app = num(clean(p.property_application_fee));
  const admin = num(clean(p.property_admin_fee));
  const moveIn: FeeLine[] = [];
  const monthly: FeeLine[] = [];
  const addOns: FeeLine[] = [];

  if (items.length) {
    const line = (x: FeeItem): FeeLine => {
      const lo = num(x.amount);
      const hi = num(x.amount_max);
      const amount = clean(x.amount_raw) || (lo != null ? (hi && hi > lo ? `${cents(lo)}–${cents(hi)}` : cents(lo)) : 'Varies');
      return { name: x.name!, amount, applied: 'Per unit', freq: /month/i.test(x.timing || '') ? 'Monthly' : 'One-time', note: clean(x.description) || undefined, lo: lo ?? undefined, hi: hi ?? lo ?? undefined };
    };
    items.forEach((x) => {
      const l = line(x);
      if (String(x.is_required) !== 'true') addOns.push(l);
      else if (l.freq === 'Monthly') monthly.push(l);
      else moveIn.push(l);
    });
  } else {
    if (admin) moveIn.push({ name: 'Administrative Fee', amount: money(admin)!, applied: 'Per unit', freq: 'One-time', lo: admin, hi: admin });
    if (app) moveIn.push({ name: 'Application Fee', amount: money(app)!, applied: 'Each adult 18+', freq: 'One-time', lo: app, hi: app });
    if (f.deposit)
      moveIn.push({
        name: 'Security Deposit',
        amount: f.deposit[0],
        applied: 'Per unit',
        freq: 'One-time',
        note: `${/varies/i.test(f.deposit[0]) ? 'Subject to screening. ' : ''}Will not exceed the legal maximum.${f.depositAlt ? ' A deposit alternative is available.' : ''}`,
      });
    const info = stripHtml(clean(p.property_fees_deposit_info) || '');
    const m = /required monthly charges:\s*([\s\S]+?)(?:;|\.(?=\s|$))/i.exec(info);
    m?.[1].split(/,\s*/).forEach((item) => {
      const amounts = [...item.matchAll(/\$([\d,]+(?:\.\d+)?)\s*(?:\(([^)]+)\))?/g)];
      if (!amounts.length) return;
      const label = item.slice(0, item.indexOf('$')).trim().replace(/[:\-–]+$/, '').trim();
      const key = Object.keys(NAMES).find((k) => label.toLowerCase() === k || label.toLowerCase() === `${k} package` || label.toLowerCase() === `${k} fee`);
      const vals = amounts.map((a) => num(a[1])!);
      const lo = Math.min(...vals);
      const hi = Math.max(...vals);
      const note = amounts.length > 1 && amounts.every((a) => a[2]) ? amounts.map((a) => `${a[2][0].toUpperCase()}${a[2].slice(1)} ${cents(num(a[1])!)}`).join(' · ') : undefined;
      monthly.push({ name: key ? NAMES[key] : titleWords(label), amount: hi > lo ? `${cents(lo)}–${cents(hi)}` : cents(lo), applied: 'Per unit', freq: 'Monthly', note, lo, hi });
    });
  }

  if (!items.length && p.property_ispetfriendly) {
    const two = petFeeTiers(p);
    const fee = num(clean(p.property_upfront_pet_fee));
    if (two) {
      addOns.push({ name: 'Pet Fee', amount: money(two[0])!, applied: '1 pet', freq: 'One-time' });
      addOns.push({ name: 'Pet Fee', amount: money(two[1])!, applied: '2 pets', freq: 'One-time' });
    } else if (fee) addOns.push({ name: 'Pet Fee', amount: money(fee)!, applied: 'Per pet', freq: 'One-time' });
    const rent = num(clean(p.property_monthly_pet_rent));
    if (rent) addOns.push({ name: 'Pet Rent', amount: money(rent)!, applied: 'Per pet', freq: 'Monthly', lo: rent, hi: rent });
  }
  if (!items.length && /renters insurance/i.test(clean(p.property_fees_deposit_info) || '')) addOns.push({ name: 'Renters Insurance', amount: 'Varies', applied: 'Per unit', freq: 'Monthly' });

  const sum = (lines: FeeLine[], k: 'lo' | 'hi') => (lines.some((l) => l[k] != null) ? lines.reduce((s, l) => s + (l[k] ?? 0), 0) : null);
  return {
    moveIn,
    monthly,
    addOns,
    moveInTotal: app || admin ? (app || 0) + (admin || 0) : null,
    monthlyLo: sum(monthly, 'lo'),
    monthlyHi: sum(monthly, 'hi'),
  };
}

export const fmtCents = cents;
/** "$137.94" or "$232.94–$252.94" */
export const moneyRange = (lo: number | null, hi: number | null) => (lo == null ? '' : hi != null && hi > lo + 0.001 ? `${cents(lo)}–${cents(hi)}` : cents(lo));

export const hasFees = (f: FeeFacts) => !!(f.appFee || f.adminFee || f.deposit || f.monthlyRange || f.raw);

/** The four pet cells shared by the amenities and floor plans pages: [label, value, note]. */
export function petCells(p: Record<string, unknown>): Array<[string, string, string]> {
  const kinds = [p.property_allows_dogs && 'Dogs', p.property_allows_cats && 'cats'].filter(Boolean) as string[];
  const welcome = kinds.length === 2 ? 'Dogs & cats' : kinds[0] ? kinds[0][0].toUpperCase() + kinds[0].slice(1) : 'Pets';
  const maxPets = num(p.property_max_pets);
  const weight = num(p.property_max_pet_weight_limit);
  const fee = num(p.property_upfront_pet_fee);
  const dep = num(p.property_pet_deposit);
  const rent = num(p.property_monthly_pet_rent);
  return [
    ['Welcome', welcome, ''],
    ['Up to', maxPets ? `${maxPets} pet${maxPets === 1 ? '' : 's'}` : 'Ask the leasing team', weight ? `under ${weight} lb` : ''],
    ['One-time fee', fee ? `From ${money(fee)}` : 'Ask the leasing team', fee ? `per pet${dep ? ` + ${money(dep)} deposit` : ''}` : ''],
    ['Pet rent', rent ? money(rent)! : 'Ask the leasing team', rent ? 'per pet, monthly' : ''],
  ];
}

/** "Dogs & cats · up to 2 · $35/mo per pet" */
export function petSummary(p: Record<string, unknown>): string {
  const [welcome, upTo, , rent] = petCells(p);
  const rentN = num(p.property_monthly_pet_rent);
  return [welcome[1], num(p.property_max_pets) ? `up to ${num(p.property_max_pets)}` : '', rentN ? `${rent[1]}/mo per pet` : ''].filter(Boolean).join(' · ') || upTo[1];
}
